import type { ReactElement, ReactNode } from 'react';
import React, { Children } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import flattenChildren from 'react-keyed-flatten-children';

import { useResponsiveGutterSpacing } from '../../../hooks';
import { COLORS } from '@garnish/constants';
import { List, ListItem } from '../../List';

type VStackProps = Readonly<{
  divider?: boolean;
  dividerColor?: string;
  customSpacing?: number;
  children: ReactNode;
}> &
  ViewProps;

/**
 * VStack - Used to add spacing between elements in vertical direction
 */
export const VStack = React.memo((props: VStackProps): ReactElement => {
  const {
    children,
    customSpacing,
    divider = false,
    dividerColor,
    ...rest
  } = props;

  /**
   * Similar to React's built-in Children.toArray method, this utility takes
   * children and returns them as an array for introspection or filtering.
   * Different from Children.toArray, it will flatten arrays and React.Fragments
   * into a regular, one-dimensional array while ensuring element and fragment
   * keys are preserved, unique, and stable between renders.
   *
   * @see {@link: https://github.com/grrowl/react-keyed-flatten-children}
   */
  const flatChildren = flattenChildren(children);

  return (
    <List {...rest}>
      {Children.map(flatChildren, (child, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <ListItem key={`stack-item-${index}`}>
          {isFirst(index) ? undefined : (
            <Spacer
              divider={divider}
              dividerColor={dividerColor}
              customSpacing={customSpacing}
            />
          )}
          {child}
        </ListItem>
      ))}
    </List>
  );
});

//
// ─── utils, etc ──────────────────────────────────────────────────────────────────
//

const isFirst = (index: number): boolean => index === 0;

type SpacerProps = Readonly<{
  customSpacing?: number;
  divider: boolean;
  dividerColor?: string;
}>;

const Spacer = (props: SpacerProps): ReactElement => {
  const { divider, dividerColor, customSpacing } = props;
  const spacerHeight = useVStackSpacerStyles(divider, customSpacing);

  return (
    <View
      testID="stack-spacer-test-id"
      style={[styles.spacerBase, { height: spacerHeight }]}
    >
      {divider ? <StackDivider color={dividerColor} /> : null}
    </View>
  );
};

const useVStackSpacerStyles = (divider?: boolean, customSpacing?: number) => {
  const standardGutter = useResponsiveGutterSpacing();
  const dividerAwareGutter = divider ? standardGutter * 2 : standardGutter;
  const spacing = customSpacing ?? dividerAwareGutter;

  return spacing;
};

type DividerProps = Readonly<{ color?: string }>;
export const StackSpacer = Spacer;
export const StackDivider = (props: DividerProps): ReactElement => {
  const maybeDividerColor = props.color ? { backgroundColor: props.color } : {};

  return (
    <View
      testID="stack-divider-test-id"
      style={[styles.dividerBase, maybeDividerColor]}
    />
  );
};

const styles = StyleSheet.create({
  spacerBase: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  dividerBase: {
    backgroundColor: COLORS.NEUTRAL_3,
    height: 1,
    width: '100%',
  },
});
